import { Link } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SparkleBackground } from './sparkle-background'

interface HeroBackgroundProps {
  children: React.ReactNode
  className?: string
  intensity?: 'subtle' | 'medium' | 'strong'
}

function HeroBackground({ children, className, intensity = 'medium' }: HeroBackgroundProps) {
  const intensityOpacity = {
    subtle: 'opacity-30',
    medium: 'opacity-50',
    strong: 'opacity-70'
  }

  return (
    <div className={cn("relative min-h-screen w-full overflow-hidden bg-background", className)}>
      {/* Gradient overlays */}
      <div className={cn("absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10", intensityOpacity[intensity])} />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,hsl(var(--accent)/0.1),transparent_50%)]" />
      
      {/* Sparkle animation */}
      <SparkleBackground />
      
      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  )
}

interface AnnouncementBanner {
  text: string
  linkText: string
  linkHref?: string
  onClick?: () => void
}

interface CallToAction {
  text: string
  href?: string
  onClick?: () => void
  variant: 'primary' | 'secondary'
  disabled?: boolean
  badge?: string
  className?: string
}

interface HeroLandingProps {
  title: string
  titleHighlight?: string
  description: string
  announcementBanner?: AnnouncementBanner
  callToActions?: CallToAction[]
  titleSize?: 'small' | 'medium' | 'large'
  className?: string
  intensity?: 'subtle' | 'medium' | 'strong'
  showScrollCta?: boolean
}

const defaultProps: Partial<HeroLandingProps> = {
  titleSize: "large",
  intensity: "strong",
  showScrollCta: false,
  callToActions: [
    { text: "Get started", href: "#", variant: "primary" },
    { text: "Learn more", href: "#", variant: "secondary" }
  ]
}

export function HeroLanding(props: HeroLandingProps) {
  const {
    title,
    titleHighlight,
    description,
    announcementBanner,
    callToActions,
    titleSize,
    className,
    intensity,
    showScrollCta
  } = { ...defaultProps, ...props }

  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    })
  }

  const renderTitle = () => {
    if (!titleHighlight) return title
    
    const parts = title.split(titleHighlight)
    if (parts.length === 1) return title
    
    return (
      <>
        {parts[0]}
        <span className="text-primary">{titleHighlight}</span>
        {parts[1]}
      </>
    )
  }

  const getTitleSizeClasses = () => {
    switch (titleSize) {
      case 'small':
        return 'text-xl sm:text-2xl md:text-4xl lg:text-5xl'
      case 'medium':
        return 'text-2xl sm:text-3xl md:text-5xl lg:text-6xl'
      case 'large':
      default:
        return 'text-2xl sm:text-4xl md:text-5xl lg:text-7xl'
    }
  }

  const renderCallToAction = (cta: CallToAction, index: number) => {
    const primaryClasses = "rounded-md bg-foreground px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold text-background shadow-[0_8px_30px_rgb(124,58,237,0.4)] hover:shadow-[0_8px_40px_rgb(124,58,237,0.5)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
    const secondaryClasses = "rounded-md border border-border bg-transparent px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold text-foreground hover:bg-secondary transition-all duration-200"
    const disabledClasses = "rounded-md bg-muted px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold text-muted-foreground cursor-not-allowed opacity-60"

    const className = cn(
      cta.variant === 'primary' ? primaryClasses : secondaryClasses,
      cta.className
    )
    
    const buttonContent = (
      <span className="flex items-center gap-2">
        {cta.text}
        {cta.badge && (
          <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">
            {cta.badge}
          </span>
        )}
      </span>
    )
    
    if (cta.disabled) {
      return (
        <button
          key={index}
          disabled
          className={disabledClasses}
        >
          {buttonContent}
        </button>
      )
    }
    
    if (cta.onClick) {
      return (
        <button
          key={index}
          onClick={cta.onClick}
          className={className}
        >
          {buttonContent}
        </button>
      )
    }
    
    if (cta.variant === 'primary') {
      return (
        <Link
          key={index}
          to={cta.href || '#'}
          className={className}
        >
          {buttonContent}
        </Link>
      )
    } else {
      return (
        <Link
          key={index}
          to={cta.href || '#'}
          className={className}
        >
          {buttonContent}
        </Link>
      )
    }
  }

  return (
    <HeroBackground intensity={intensity} className={className}>
      {/* Hero content */}
      <div className="relative isolate px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
        <div className="mx-auto max-w-2xl py-8 sm:py-12">
          {/* Announcement banner */}
          {announcementBanner && (
            <div className="hidden sm:mb-8 sm:flex sm:justify-center">
              <div className="relative rounded-full px-3 py-1 text-sm text-muted-foreground ring-1 ring-border hover:ring-foreground/20">
                {announcementBanner.text}{' '}
                {announcementBanner.onClick ? (
                  <button 
                    onClick={announcementBanner.onClick} 
                    className="font-semibold text-primary"
                  >
                    {announcementBanner.linkText} <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <Link to={announcementBanner.linkHref || '#'} className="font-semibold text-primary">
                    <span aria-hidden="true" className="absolute inset-0" />
                    {announcementBanner.linkText} <span aria-hidden="true">→</span>
                  </Link>
                )}
              </div>
            </div>
          )}

          <div className="text-center">
            <h1
              className={`font-semibold tracking-tight text-balance text-foreground ${getTitleSizeClasses()}`}
            >
              {renderTitle()}
            </h1>
            <p className="mt-4 sm:mt-6 md:mt-8 text-base sm:text-lg font-medium text-pretty text-muted-foreground md:text-xl/8">
              {description}
            </p>

            {/* Call to action buttons */}
            {callToActions && callToActions.length > 0 && (
              <div className="mt-6 sm:mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-x-6">
                {callToActions.map((cta, index) => renderCallToAction(cta, index))}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Scroll CTA - positioned at absolute bottom center */}
      {showScrollCta && (
        <button
          onClick={handleScrollDown}
          className="absolute bottom-6 sm:bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 text-muted-foreground/50 hover:text-foreground transition-colors duration-300 cursor-pointer"
          aria-label="Scroll to explore"
        >
          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 animate-bounce" />
        </button>
      )}
    </HeroBackground>
  )
}

export type { HeroLandingProps, AnnouncementBanner, CallToAction }